import React from "react";
import { MessageSquare } from "lucide-react";
import { SheetDetail, ItemDetail } from "../types";

interface InspectionCardContentProps {
  currentSheet: SheetDetail | undefined;
  isDarkMode: boolean;
  contentRef: (el: HTMLDivElement | null) => void;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

export const InspectionCardContent: React.FC<InspectionCardContentProps> = ({
  currentSheet,
  isDarkMode,
  contentRef,
  onScroll,
}) => {
  const getResponseColor = (response?: string): string => {
    if (!response) return "bg-gray-200 text-gray-700";
    if (response === "pass") return "bg-green-500 text-white";
    if (response === "fail") return "bg-red-500 text-white";
    if (response === "na") return "bg-gray-400 text-white";
    if (response === "checked") return "bg-blue-500 text-white";
    return "bg-gray-200 text-gray-700";
  };

  const getResponseText = (response?: string): string => {
    if (!response) return "No Response";
    if (response === "pass") return "Pass";
    if (response === "fail") return "Fail";
    if (response === "na") return "N/A";
    if (response === "checked") return "✓";
    return response;
  };

  // Group items by section
  const itemsBySection: { [key: string]: ItemDetail[] } = {};
  if (currentSheet?.items) {
    currentSheet.items.forEach((item) => {
      if (!itemsBySection[item.section_name]) {
        itemsBySection[item.section_name] = [];
      }
      itemsBySection[item.section_name].push(item);
    });
  }

  return (
    <div
      ref={contentRef}
      className="flex-1 overflow-y-auto p-2"
      style={{
        overflowY: "auto",
        scrollBehavior: "auto",
      }}
      onScroll={onScroll}
    >
      {currentSheet ? (
        <div className="space-y-2">
          {Object.keys(itemsBySection).map((sectionName) => (
            <div
              key={sectionName}
              className={`rounded p-2 ${
                isDarkMode ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <h4
                className={`text-sm font-bold mb-1.5 ${
                  isDarkMode ? "text-phoenix-400" : "text-phoenix-700"
                }`}
              >
                {sectionName}
              </h4>
              <div className="space-y-1.5">
                {itemsBySection[sectionName].map((item) => (
                  <div
                    key={item.id}
                    className={`p-2 rounded text-sm transition-all duration-200 cursor-pointer ${
                      isDarkMode
                        ? "bg-gray-600 hover:ring-2 hover:ring-orange-500 hover:ring-offset-2 hover:ring-offset-gray-700"
                        : "bg-white hover:ring-2 hover:ring-orange-500 hover:ring-offset-2"
                    }`}
                    title={item.item_name}
                  >
                    <div className="flex justify-between items-center">
                      <span
                        className={`flex-1 font-medium ${
                          isDarkMode ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        {item.item_name}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded text-xs font-semibold ${getResponseColor(
                          item.response
                        )}`}
                      >
                        {getResponseText(item.response)}
                      </span>
                    </div>
                    {item.notes && (
                      <div className="mt-1.5 flex items-start gap-1.5">
                        <MessageSquare
                          className={`w-3 h-3 flex-shrink-0 mt-0.5 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                        />
                        <p
                          className={`text-xs leading-snug ${
                            isDarkMode ? "text-blue-300" : "text-blue-700"
                          }`}
                        >
                          {item.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className={`flex items-center justify-center h-full text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Loading details...
        </div>
      )}
    </div>
  );
};
